import type { ApiChatMessage } from "@/api/rest/services/chatService";
import type { Dayjs } from "@/components/dayjsExtended";

export enum CaseType {
  WARRANTY = "WARRANTY",
  PROPERTY_MANAGER_CASE = "PROPERTY_MANAGER_CASE",
}

// src/api/rest/models/warranty.ts
export interface CaseRaw {
  id: number;
  householdId: number;
  title: string;
  status: CaseStatus;
  createdAt: string;
  type: CaseType;
  lastMessageTimestamp?: string;
  subcontractorName?: string | null;
  subcontractorEmail?: string | null;
  categoryName?: string | null;
  needsBuilderReply?: boolean;
  inspection?: CaseInspection | null;
}

/** End-of-warranty inspection a defect case was found in; cases sharing an id form one group. */
export interface CaseInspection {
  id: number;
  title: string;
  externalNumber: string | null;
  inspectedAt: string;
}

export interface Case extends CaseRaw {
  createdAtTimestamp: Dayjs;
  localisedStatus: string;
}

export enum CaseStatus {
  SOLVED = "SOLVED",
  CLOSED = "CLOSED",
  BACKLOG = "BACKLOG",
  TO_DO = "TO_DO",
  WAITING = "WAITING",
  IN_PROGRESS = "IN_PROGRESS",
  REVIEW = "REVIEW",
  NOT_DONE = "NOT_DONE",
}

export const statusMap = new Map<CaseType, Map<CaseStatus, string>>([
  [
    CaseType.WARRANTY,
    new Map([
      [CaseStatus.BACKLOG, "warranty.caseStatus.backlog"],
      [CaseStatus.TO_DO, "warranty.caseStatus.toDo"],
      [CaseStatus.WAITING, "warranty.caseStatus.waiting"],
      [CaseStatus.IN_PROGRESS, "warranty.caseStatus.inProgress"],
      [CaseStatus.REVIEW, "warranty.caseStatus.review"],
      [CaseStatus.SOLVED, "warranty.caseStatus.solved"],
      [CaseStatus.CLOSED, "warranty.caseStatus.closed"],
      [CaseStatus.NOT_DONE, "warranty.caseStatus.notDone"],
    ]),
  ],
  [
    CaseType.PROPERTY_MANAGER_CASE,
    new Map([
      [CaseStatus.BACKLOG, "propertyManagerCase.caseStatus.backlog"],
      [CaseStatus.TO_DO, "propertyManagerCase.caseStatus.toDo"],
      [CaseStatus.WAITING, "propertyManagerCase.caseStatus.waiting"],
      [CaseStatus.IN_PROGRESS, "propertyManagerCase.caseStatus.inProgress"],
      [CaseStatus.REVIEW, "propertyManagerCase.caseStatus.review"],
      [CaseStatus.SOLVED, "propertyManagerCase.caseStatus.solved"],
      [CaseStatus.CLOSED, "propertyManagerCase.caseStatus.closed"],
      [CaseStatus.NOT_DONE, "propertyManagerCase.caseStatus.notDone"],
    ]),
  ],
]);

export interface CaseMessageRaw {
  id: number;
  warrantyCaseId: number;
  accountId: number;
  userId: number | null;
  userName: string | null;
  createdAt: string;
  content: string;
  type: CaseMessageType;
  externalCreatorInfo?: Record<string, any>;
  mimeType?: string | null;
}

export interface CaseMessage extends CaseMessageRaw {
  createdAtTimestamp: Dayjs;
}

export enum CaseMessageType {
  TEXT = "TEXT",
  FILE = "FILE",
  IMAGE = "IMAGE",
  AI_HISTORY = "AI_HISTORY",
}

export interface CaseResponse {
  case: Case;
  messages: CaseMessage[];
}

export interface CaseMessageRequest {
  accountId: number;
  message: string;
}

export interface NewCaseRequest {
  question: string;
  householdId: number;
  history: ApiChatMessage[];
  accountId: number;
  type: CaseType;
  draftId?: number | null;
  flowType?: string | null;
}

export interface SubmitCaseRequestWithoutFiles {
  title: string;
  description: string;
  householdId: number;
  accountId: number;
  phoneNumber: string | null;
  type: CaseType;
  subcontractorName?: string | null;
  subcontractorEmail?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  likelyUnderWarranty?: boolean | null;
  draftId?: number | null;
  // Same id for every retry of one submission, so the backend creates the case once.
  clientSubmissionId?: string;
}

export interface UpdateCaseStatusRequest {
  buildingId: number;
  status: CaseStatus;
}

export enum NewWarrantyCaseResponseType {
  MORE_INFO_REQUIRED = "MORE_INFO_REQUIRED",
  WARRANTY = "WARRANTY",
  NO_WARRANTY = "NO_WARRANTY",
  PROPERTY_MANAGER_CASE = "PROPERTY_MANAGER_CASE",
  NO_PROPERTY_MANAGER_CASE = "NO_PROPERTY_MANAGER_CASE",
}

export interface NewWarrantyCaseResponse {
  status: NewWarrantyCaseResponseType;
  message: string;
  caseId: number | null;
  provisionalTitle: string | null;
  draftId?: number | null;
  // Problems the homeowner mentioned beyond this draft; filed next, one case each.
  otherIssues?: string[];
  // What the builder's conditions say about this kind of problem; shown to the homeowner, never filed.
  builderNote?: string | null;
}

export interface WarrantySelfCheckRequest {
  householdId: number;
  accountId: number;
  // The draft the homeowner is about to submit.
  description: string;
}

// Steps from the home's documents the homeowner can try first; empty when there is nothing to try.
export interface WarrantySelfCheckResponse {
  steps: string[];
  documentIds: number[];
}
